import React from 'react';
import { backgroundColorOptions, accentColorOptions } from './theme/colors';

const ToolCard = ({ image, title, description, accent, paddingClass }) => {
  return (
    <div className="group relative h-full rounded-3xl border border-slate-200/70 dark:border-white/10 bg-white/90 dark:bg-slate-900/40 shadow-xs transition-transform duration-300 hover:-translate-y-1.5 hover:shadow-xl overflow-hidden">
      {image && (
        <div className="h-48 w-full overflow-hidden">
          <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        </div>
      )}
      <div className={`${paddingClass} space-y-3`}>
        {title && (
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <span className={`inline-flex h-2.5 w-2.5 rounded-full bg-current ${accent.textClass || 'text-blue-600'}`} style={accent.style} />
            {title}
          </h3>
        )}
        {description && (
          <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

const ToolsSectionComponent = ({
  showHeader = true,
  title = 'Learn more about our top recruiter tools.',
  titleColor = 'gray-900',
  titleSize = 'xl',
  titleAlignment = 'center',
  cards = [],
  cardsPerRow = 4,
  cardSpacing = 'lg',
  cardPadding = 'lg',
  backgroundColor = 'white',
  padding = 'xl',
  maxWidth = '7xl',
  accentColor = 'blue'
}) => {
  // Removed color resolution - now using Tailwind dark mode classes
  const accent = { textClass: 'text-blue-600' }; // Keep accent for styling

  const titleSizeClasses = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl'
  };

  const titleAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  const getGridCols = () => {
    const mapping = {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
      5: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
      6: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'
    };
    return mapping[cardsPerRow] || mapping[4];
  };

  const spacingMap = {
    sm: 'gap-6',
    md: 'gap-8',
    lg: 'gap-10',
    xl: 'gap-12',
    '2xl': 'gap-14'
  };

  const paddingMap = {
    sm: 'py-8 px-4',
    md: 'py-12 px-6',
    lg: 'py-16 px-8',
    xl: 'py-20 px-8',
    '2xl': 'py-24 px-8'
  };

  const cardPaddingMap = {
    sm: 'p-6',
    md: 'p-7',
    lg: 'p-8',
    xl: 'p-10',
    '2xl': 'p-12'
  };

  const maxWidthMap = {
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-full'
  };

  return (
    <section className="bg-white dark:bg-slate-900 w-full font-sf-pro">
      <div className={`${maxWidthMap[maxWidth] || 'max-w-7xl'} mx-auto ${paddingMap[padding] || 'py-20 px-8'}`}>
        {showHeader && title && (
          <div className={`mb-12 ${titleAlignClass[titleAlignment] || 'text-center'} space-y-3`}>
            <span className={`${accent.textClass || 'text-blue-600'} text-sm font-semibold uppercase tracking-[0.2em]`} style={accent.style}>
              Tools
            </span>
            <h2 className={`${titleSizeClasses[titleSize] || 'text-xl'} font-bold ${titleColor === 'gray-900' ? 'text-slate-900 dark:text-slate-100' : 'text-slate-800'}`}>
              {title}
            </h2>
          </div>
        )}

        <div className={`grid ${getGridCols()} ${spacingMap[cardSpacing] || 'gap-10'}`}>
          {cards.map((card, index) => (
            <ToolCard
              key={index}
              image={card.image}
              title={card.title}
              description={card.description}
              accent={accent}
              paddingClass={cardPaddingMap[cardPadding] || 'p-8'}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export const ToolsSection = {
  fields: {
    showHeader: {
      type: 'radio',
      label: 'Header anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    title: {
      type: 'text',
      label: 'Titel'
    },
    titleColor: {
      type: 'select',
      label: 'Titel-Farbe',
      options: [
        { label: 'Dunkel', value: 'gray-900' },
        { label: 'Schwarz', value: 'black' }
      ]
    },
    titleSize: {
      type: 'select',
      label: 'Titel-Größe',
      options: [
        { label: 'Klein', value: 'sm' },
        { label: 'Normal', value: 'base' },
        { label: 'Groß', value: 'lg' },
        { label: 'Extra Groß', value: 'xl' },
        { label: '2XL', value: '2xl' },
        { label: '3XL', value: '3xl' },
        { label: '4XL', value: '4xl' }
      ]
    },
    titleAlignment: {
      type: 'select',
      label: 'Titel-Ausrichtung',
      options: [
        { label: 'Links', value: 'left' },
        { label: 'Zentriert', value: 'center' },
        { label: 'Rechts', value: 'right' }
      ]
    },
    cards: {
      type: 'array',
      label: 'Tool-Karten',
      arrayFields: {
        image: {
          type: 'text',
          label: 'Bild URL',
          placeholder: 'https://example.com/image.jpg'
        },
        title: {
          type: 'text',
          label: 'Titel'
        },
        description: {
          type: 'textarea',
          label: 'Beschreibung'
        }
      },
      getItemSummary: (item) => item.title || 'Tool-Karte'
    },
    cardsPerRow: {
      type: 'select',
      label: 'Karten pro Zeile',
      options: [
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
        { label: '5', value: 5 },
        { label: '6', value: 6 }
      ]
    },
    cardSpacing: {
      type: 'select',
      label: 'Karten-Abstand',
      options: [
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Extra Groß', value: 'xl' },
        { label: 'Sehr Groß', value: '2xl' }
      ]
    },
    cardPadding: {
      type: 'select',
      label: 'Karten-Innenabstand',
      options: [
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Extra Groß', value: 'xl' },
        { label: 'Sehr Groß', value: '2xl' }
      ]
    },
    backgroundColor: {
      type: 'select',
      label: 'Hintergrundfarbe',
      options: backgroundColorOptions()
    },
    padding: {
      type: 'select',
      label: 'Innenabstand',
      options: [
        { label: 'Klein', value: 'sm' },
        { label: 'Mittel', value: 'md' },
        { label: 'Groß', value: 'lg' },
        { label: 'Extra Groß', value: 'xl' },
        { label: '2XL', value: '2xl' }
      ]
    },
    maxWidth: {
      type: 'select',
      label: 'Maximale Breite',
      options: [
        { label: '4XL', value: '4xl' },
        { label: '5XL', value: '5xl' },
        { label: '6XL', value: '6xl' },
        { label: '7XL', value: '7xl' },
        { label: 'Vollbreite', value: 'full' }
      ]
    },
    accentColor: {
      type: 'select',
      label: 'Akzentfarbe',
      options: accentColorOptions(['blue', 'indigo', 'purple', 'green', 'amber', 'pink', 'motion-yellow', 'motion-cyan', 'motion-lime', 'motion-pink', 'motion-blue'])
    }
  },
  defaultProps: {
    showHeader: true,
    title: 'Learn more about our top recruiter tools.',
    titleColor: 'gray-900',
    titleSize: 'xl',
    titleAlignment: 'center',
    cards: [],
    cardsPerRow: 4,
    cardSpacing: 'lg',
    cardPadding: 'lg',
    backgroundColor: 'white',
    padding: 'xl',
    maxWidth: '7xl',
    accentColor: 'blue'
  },
  render: ToolsSectionComponent
};
